import React from 'react';
import { X, Trash2, Zap, Sparkles, Send, ChevronRight } from 'lucide-react';
import { Message } from '../../constants/MagnusResolveAssistantConstants';

interface ChatPanelProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    isPrimeMode: boolean;
    setIsPrimeMode: (prime: boolean) => void;
    handleClear: () => void;
    messages: Message[];
    isThinking: boolean;
    inputValue: string;
    setInputValue: (val: string) => void;
    handleSend: () => void;
    handleAction: (action: any) => void;
    messagesEndRef: React.RefObject<HTMLDivElement>;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
    isOpen,
    setIsOpen,
    isPrimeMode,
    setIsPrimeMode,
    handleClear,
    messages,
    isThinking,
    inputValue,
    setInputValue,
    handleSend,
    handleAction,
    messagesEndRef
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed bottom-24 right-6 z-50 w-96 h-[600px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-purple-200 dark:border-purple-700">
            {/* Header */}
            <div className={`p-4 flex items-center justify-between transition-colors duration-500 ${isPrimeMode
                ? 'bg-gradient-to-r from-slate-900 via-purple-900 to-slate-800'
                : 'bg-gradient-to-r from-purple-500 to-blue-600'}`}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        {isPrimeMode ? <Zap className="text-yellow-400" size={20} /> : <Sparkles className="text-white" size={20} />}
                    </div>
                    <div>
                        <h3 className="text-white font-bold text-lg">Magnus</h3>
                        <p className="text-purple-100 text-xs">Reliability Assistant</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsPrimeMode(!isPrimeMode)}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${isPrimeMode
                            ? 'bg-yellow-400 text-purple-900 border-yellow-300 shadow-[0_0_10px_rgba(250,204,21,0.5)]'
                            : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                            }`}
                    >
                        {isPrimeMode ? '🚀 PRIME' : '⚡ BASIC'}
                    </button>
                    <button onClick={handleClear} className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors" title="Clear Chat">
                        <Trash2 size={16} />
                    </button>
                    <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors">
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-slate-900">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-2xl p-3 ${msg.role === 'user'
                            ? 'bg-blue-600 text-white rounded-br-none'
                            : 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-bl-none shadow-sm'
                            }`}>
                            <div className="text-sm whitespace-pre-wrap">
                                {msg.content.split(/(\*\*.*?\*\*)/g).map((part, i) => {
                                    if (part.startsWith('**') && part.endsWith('**')) {
                                        return <strong key={i} className="text-purple-600 dark:text-purple-400">{part.slice(2, -2)}</strong>;
                                    }
                                    return <span key={i}>{part}</span>;
                                })}
                            </div>
                            {msg.action && (
                                <button
                                    onClick={() => handleAction(msg.action)}
                                    className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                                >
                                    {msg.action.label}
                                    <ChevronRight size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {isThinking && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <div className="animate-pulse">🤖</div>
                        <div className="text-sm">Magnus is thinking...</div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder={isPrimeMode ? "Ask anything about your data..." : "Ask a question or request a page..."}
                        className="flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        disabled={isThinking}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!inputValue.trim() || isThinking}
                        className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send size={20} />
                    </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    {isPrimeMode ? '🔮 Prime: Deep AI analysis' : '⚡ Basic: Quick navigation & answers'}
                </p>
            </div>
        </div>
    );
};
